import { useEffect, useState, useCallback, useMemo } from 'react';
import { trackRender } from '../utils/debugRender';
import { useParams, Link } from 'react-router-dom';
import { usersApi, analyticsApi, databaseApi } from '../services/api';
import toast from 'react-hot-toast';
import {
  Tabs,
  TabItem,
  Card,
  Badge,
  Timeline,
  TimelineItem,
  TimelinePoint,
  TimelineContent,
  TimelineTime,
  TimelineTitle,
  TimelineBody,
  Button,
  Alert
} from 'flowbite-react';
import {
  UserIcon,
  CreditCardIcon,
  ChartBarIcon,
  DocumentTextIcon,
  ArrowLeftIcon,
  PencilIcon,
  TrashIcon,
  ArrowUpIcon,
  ClockIcon,
  CalendarIcon,
  EnvelopeIcon,
  IdentificationIcon,
  ArrowPathIcon,
  InformationCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  ArrowTopRightOnSquareIcon
} from '@heroicons/react/24/outline';

export default function UserDetail() {
  const { userId } = useParams();
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [storage, setStorage] = useState(null);
  const [upgrading, setUpgrading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  // 🔍 DEBUG: Track renders to identify infinite loops
  trackRender('UserDetail', { userId, loading, activeTab });

  useEffect(() => {
    loadUserData();
  }, [userId]);

  // DEBUG: Log subscription state changes
  useEffect(() => {
    if (user) {
      console.log('[USER DETAIL STATE] Subscription state:', {
        hasSubscription: !!user?.subscription,
        subscription: user?.subscription
      });
    }
  }, [user?.subscription]);

  // 🔍 FIX: Memoize timestamp string to prevent Alert re-render on every render
  const cachedTimestamp = useMemo(() => {
    return user?.subscriptionSync?.lastSynced
      ? new Date(user.subscriptionSync.lastSynced).toLocaleString()
      : '';
  }, [user?.subscriptionSync?.lastSynced]);

  // Helper function for price formatting
  function formatPrice(amount, currency, interval) {
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase()
    });
    const price = formatter.format(amount / 100);
    const intervalLabel = interval === 'month' ? 'month' : 'year';
    return `${price}/${intervalLabel}`;
  }

  async function loadUserData() {
    try {
      setLoading(true);
      const [userData, analyticsData, storageData] = await Promise.all([
        usersApi.get(userId),
        analyticsApi.user(userId),
        databaseApi.userStorage(userId)
      ]);

      // DEBUG: Log received data
      console.log('[USER DETAIL] Received user data:', {
        userId: userData?.clerk?.userId,
        email: userData?.clerk?.email,
        hasSubscription: !!userData?.subscription,
        subscription: userData?.subscription
      });

      setUser(userData);
      setAnalytics(analyticsData);
      setStorage(storageData);
    } catch (error) {
      console.error('Failed to load user data:', error);
      toast.error('Failed to load user details');
    } finally {
      setLoading(false);
    }
  }

  async function handleUpgrade(tier) {
    if (!confirm(`Upgrade user to ${tier} plan?`)) return;

    const reason = prompt('Reason for upgrade:');
    if (!reason) return;

    try {
      setUpgrading(true);
      await usersApi.upgrade(userId, tier, reason);
      toast.success(`User upgraded to ${tier}`);
      await loadUserData();
    } catch (error) {
      console.error('Failed to upgrade user:', error);
      toast.error('Failed to upgrade user');
    } finally {
      setUpgrading(false);
    }
  }

  async function handleSyncStripe() {
    try {
      setSyncing(true);
      await usersApi.syncStripe(userId);
      toast.success('Synced with Stripe successfully');
      await loadUserData(); // Refresh all data
    } catch (error) {
      console.error('Failed to sync with Stripe:', error);
      toast.error('Failed to sync with Stripe');
    } finally {
      setSyncing(false);
    }
  }

  // 🔍 DEBUG: Memoize Tabs callback to prevent infinite loops
  const handleTabChange = useCallback((tab) => {
    console.log('🔄 Tab changed to:', tab);
    setActiveTab(tab);
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return (
      <Card className="text-center py-12">
        <p className="text-gray-600 mb-4">User not found</p>
        <Link to="/users" className="text-primary hover:underline inline-flex items-center gap-2">
          <ArrowLeftIcon className="h-4 w-4" />
          Back to users
        </Link>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with User Info */}
      <Card>
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex items-start gap-4 flex-1">
            <img
              src={user.clerk.imageUrl || '/avatar-placeholder.png'}
              alt="User"
              className="h-20 w-20 rounded-full ring-4 ring-gray-100"
            />
            <div className="flex-1">
              <Link
                to="/users"
                className="text-primary hover:underline mb-2 inline-flex items-center gap-1 text-sm"
              >
                <ArrowLeftIcon className="h-4 w-4" />
                Back to users
              </Link>
              <h1 className="text-3xl font-bold text-gray-900">
                {user.clerk.firstName} {user.clerk.lastName}
              </h1>
              <div className="flex items-center gap-2 mt-2">
                <EnvelopeIcon className="h-4 w-4 text-gray-400" />
                <p className="text-gray-600">{user.clerk.email}</p>
              </div>
              <div className="flex items-center gap-3 mt-3">
                <Badge color={user.subscription?.tier === 'premium' ? 'warning' : 'gray'} size="lg">
                  {user.subscription?.tier || 'free'}
                </Badge>
                <Badge
                  color={user.subscription?.status === 'active' ? 'success' : 'gray'}
                  size="lg"
                >
                  {user.subscription?.status || 'inactive'}
                </Badge>
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              color="light"
              size="sm"
              onClick={() => toast.info('Edit functionality coming soon')}
            >
              <PencilIcon className="h-4 w-4 mr-2" />
              Edit User
            </Button>
            <Button
              color="failure"
              size="sm"
              onClick={() => toast.error('Delete functionality coming soon')}
            >
              <TrashIcon className="h-4 w-4 mr-2" />
              Delete
            </Button>
          </div>
        </div>
      </Card>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">API Calls (30d)</p>
              <p className="mt-2 text-3xl font-bold text-gray-900">
                {analytics?.apiCalls30d?.toLocaleString() || 0}
              </p>
            </div>
            <ChartBarIcon className="h-10 w-10 text-blue-500" />
          </div>
        </Card>
        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Data Entries</p>
              <p className="mt-2 text-3xl font-bold text-gray-900">
                {analytics?.dataEntries?.toLocaleString() || 0}
              </p>
            </div>
            <DocumentTextIcon className="h-10 w-10 text-green-500" />
          </div>
        </Card>
        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Storage Used</p>
              <p className="mt-2 text-3xl font-bold text-gray-900">
                {storage?.storage?.mb?.toFixed(2) || 0} MB
              </p>
            </div>
            <svg className="h-10 w-10 text-purple-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
            </svg>
          </div>
        </Card>
        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Account Age</p>
              <p className="mt-2 text-3xl font-bold text-gray-900">
                {Math.floor((Date.now() - new Date(user.clerk.createdAt).getTime()) / (1000 * 60 * 60 * 24))} days
              </p>
            </div>
            <CalendarIcon className="h-10 w-10 text-orange-500" />
          </div>
        </Card>
      </div>

      {/* Tabbed Content */}
      <Card>
        <Tabs
          aria-label="User details tabs"
          variant="underline"
          onActiveTabChange={handleTabChange}
        >
          {/* Overview Tab */}
          <TabItem title="Overview" icon={UserIcon}>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 pt-4">
              {/* Profile Information */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <IdentificationIcon className="h-5 w-5" />
                  Profile Information
                </h3>
                <dl className="space-y-4">
                  <div className="flex justify-between py-3 border-b border-gray-100">
                    <dt className="text-sm font-medium text-gray-500">User ID</dt>
                    <dd className="text-sm text-gray-900 font-mono">{user.clerk.userId}</dd>
                  </div>
                  <div className="flex justify-between py-3 border-b border-gray-100">
                    <dt className="text-sm font-medium text-gray-500">Created</dt>
                    <dd className="text-sm text-gray-900">
                      {new Date(user.clerk.createdAt).toLocaleDateString()}
                    </dd>
                  </div>
                  <div className="flex justify-between py-3 border-b border-gray-100">
                    <dt className="text-sm font-medium text-gray-500">Last Sign In</dt>
                    <dd className="text-sm text-gray-900">
                      {user.clerk.lastSignIn
                        ? new Date(user.clerk.lastSignIn).toLocaleDateString()
                        : 'Never'}
                    </dd>
                  </div>
                  {user.profile && (
                    <>
                      <div className="flex justify-between py-3 border-b border-gray-100">
                        <dt className="text-sm font-medium text-gray-500">Age</dt>
                        <dd className="text-sm text-gray-900">{user.profile.age || 'N/A'}</dd>
                      </div>
                      <div className="flex justify-between py-3 border-b border-gray-100">
                        <dt className="text-sm font-medium text-gray-500">Surgery Date</dt>
                        <dd className="text-sm text-gray-900">
                          {user.profile.surgery_date || 'N/A'}
                        </dd>
                      </div>
                    </>
                  )}
                </dl>
              </div>

              {/* Storage Metrics */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
                  </svg>
                  Storage Metrics
                </h3>
                <div className="space-y-4">
                  <div className="rounded-lg bg-gradient-to-r from-blue-50 to-blue-100 p-4">
                    <p className="text-sm text-blue-700 mb-1">Total Storage</p>
                    <p className="text-2xl font-bold text-blue-900">
                      {storage?.storage?.mb?.toFixed(2) || 0} MB
                    </p>
                  </div>
                  <div className="rounded-lg bg-gradient-to-r from-green-50 to-green-100 p-4">
                    <p className="text-sm text-green-700 mb-1">Records Stored</p>
                    <p className="text-2xl font-bold text-green-900">
                      {analytics?.dataEntries?.toLocaleString() || 0}
                    </p>
                  </div>
                  <div className="rounded-lg bg-gradient-to-r from-purple-50 to-purple-100 p-4">
                    <p className="text-sm text-purple-700 mb-1">Avg. Record Size</p>
                    <p className="text-2xl font-bold text-purple-900">
                      {analytics?.dataEntries > 0
                        ? ((storage?.storage?.mb || 0) / analytics.dataEntries * 1024).toFixed(1)
                        : 0}{' '}
                      KB
                    </p>
                  </div>
                  <Button color="gray" className="w-full" onClick={() => toast.success('Export functionality coming soon')}>
                    <DocumentTextIcon className="h-4 w-4 mr-2" />
                    Export User Data
                  </Button>
                </div>
              </div>
            </div>
          </TabItem>

          {/* Activity Tab */}
          <TabItem title="Activity" icon={ClockIcon}>
            <div className="pt-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity Timeline</h3>
              {user.activity?.recentData?.length > 0 ? (
                <Timeline>
                  {user.activity.recentData.slice(0, 10).map((entry, index) => (
                    <TimelineItem key={entry.date || index}>
                      <TimelinePoint />
                      <TimelineContent>
                        <TimelineTime>
                          {entry.date} • {new Date(entry.created_at).toLocaleTimeString()}
                        </TimelineTime>
                        <TimelineTitle>Data entry recorded</TimelineTitle>
                        <TimelineBody>
                          User logged tracking data on {entry.date}
                        </TimelineBody>
                      </TimelineContent>
                    </TimelineItem>
                  ))}
                </Timeline>
              ) : (
                <div className="text-center py-12">
                  <ClockIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No recent activity</p>
                </div>
              )}
            </div>
          </TabItem>

          {/* Subscription Tab */}
          <TabItem title="Subscription" icon={CreditCardIcon}>
            <div className="p-6">
              {user?.subscription ? (
                <div className="space-y-6">
                  {/* Debug Info Card */}
                  <Alert color="info">
                    <div className="text-sm space-y-1">
                      <p><strong>Data Source:</strong></p>
                      <ul className="list-disc list-inside ml-2 space-y-1">
                        {user.subscription.createdAt ? (
                          <li>Database record: {user.subscription.tier} ({user.subscription.status})</li>
                        ) : (
                          <li className="text-gray-600">Default free plan (no database record)</li>
                        )}
                        {user.subscription.stripeCustomerId && (
                          <li>Stripe Customer ID: {user.subscription.stripeCustomerId}</li>
                        )}
                        {user.subscription.stripeSubscriptionId && (
                          <li>Stripe Subscription ID: {user.subscription.stripeSubscriptionId}</li>
                        )}
                        {user.subscription.stripePlan ? (
                          <li className="text-green-600 font-semibold">✓ Stripe plan data loaded from API</li>
                        ) : user.subscription.stripeSubscriptionId ? (
                          <li className="text-orange-600 font-semibold">⚠ Stripe subscription ID exists but plan data not loaded</li>
                        ) : (
                          <li className="text-gray-600">No Stripe subscription linked</li>
                        )}
                      </ul>
                    </div>
                  </Alert>

                  {/* Header with Sync Button */}
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-medium text-gray-900">
                      {user.subscription.tier === 'premium' ? 'Premium Plan' : 'Free Plan'}
                    </h3>
                    {user.subscription.stripeSubscriptionId && (
                      <Button
                        onClick={handleSyncStripe}
                        disabled={syncing}
                        size="sm"
                        color="gray"
                      >
                        {syncing ? (
                          <>
                            <svg className="animate-spin h-4 w-4 mr-2" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                            Syncing...
                          </>
                        ) : (
                          <>
                            <ArrowPathIcon className="h-4 w-4 mr-2" />
                            Sync with Stripe
                          </>
                        )}
                      </Button>
                    )}
                  </div>

                  {/* Warning Banners */}
                  {user.subscriptionSync?.cached && (
                    <Alert key="cached-alert" color="info">
                      <div className="flex items-start gap-2">
                        <InformationCircleIcon className="h-5 w-5 flex-shrink-0" />
                        <span>Displaying cached data from {cachedTimestamp}</span>
                      </div>
                    </Alert>
                  )}

                  {user.subscriptionSync?.mismatch && (
                    <Alert key="mismatch-alert" color="warning">
                      <div className="flex items-start gap-2">
                        <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0" />
                        <div>
                          <span className="font-medium">Status mismatch detected!</span>
                          <p className="mt-1 text-sm">
                            Database shows "{user.subscription.status}" but Stripe shows "{user.subscription.stripeStatus}".
                            Click "Sync with Stripe" to resolve.
                          </p>
                        </div>
                      </div>
                    </Alert>
                  )}

                  {user.subscriptionSync?.error && (
                    <Alert key="error-alert" color="failure">
                      <div className="flex items-start gap-2">
                        <XCircleIcon className="h-5 w-5 flex-shrink-0" />
                        <div>
                          <span className="font-medium">Failed to fetch from Stripe</span>
                          <p className="mt-1 text-sm">Showing database data only. Error: {user.subscriptionSync.error}</p>
                        </div>
                      </div>
                    </Alert>
                  )}

                  {/* Subscription Details */}
                  <dl className="divide-y divide-gray-200 bg-white border border-gray-200 rounded-lg">
                    <div className="flex justify-between py-3 px-4">
                      <dt className="text-sm font-medium text-gray-500">Status</dt>
                      <dd>
                        <Badge color={user.subscription.status === 'active' ? 'success' : 'gray'}>
                          {user.subscription.status}
                        </Badge>
                      </dd>
                    </div>

                    {/* NEW: Stripe Plan Details */}
                    {user.subscription.stripePlan && (
                      <>
                        <div className="flex justify-between py-3 px-4">
                          <dt className="text-sm font-medium text-gray-500">Product</dt>
                          <dd className="text-sm text-gray-900 font-medium">
                            {user.subscription.stripePlan.productName}
                          </dd>
                        </div>

                        <div className="flex justify-between py-3 px-4">
                          <dt className="text-sm font-medium text-gray-500">Price</dt>
                          <dd className="text-sm text-gray-900 font-semibold">
                            {formatPrice(
                              user.subscription.stripePlan.amount,
                              user.subscription.stripePlan.currency,
                              user.subscription.stripePlan.interval
                            )}
                          </dd>
                        </div>

                        {user.subscription.stripePlan.nickname && (
                          <div className="flex justify-between py-3 px-4">
                            <dt className="text-sm font-medium text-gray-500">Plan Name</dt>
                            <dd className="text-sm text-gray-900">
                              {user.subscription.stripePlan.nickname}
                            </dd>
                          </div>
                        )}

                        {user.subscription.currentPeriodEnd && (
                          <div className="flex justify-between py-3 px-4">
                            <dt className="text-sm font-medium text-gray-500">Next Billing Date</dt>
                            <dd className="text-sm text-gray-900">
                              {new Date(user.subscription.currentPeriodEnd).toLocaleDateString()}
                            </dd>
                          </div>
                        )}

                        {user.subscription.cancelAtPeriodEnd && (
                          <div className="py-3 px-4">
                            <Alert color="warning">
                              Subscription will cancel on {new Date(user.subscription.currentPeriodEnd).toLocaleDateString()}
                            </Alert>
                          </div>
                        )}
                      </>
                    )}

                    {user.subscription.stripeCustomerId && (
                      <div className="flex justify-between py-3 px-4">
                        <dt className="text-sm font-medium text-gray-500">Stripe Customer</dt>
                        <dd className="text-sm text-gray-900 font-mono">
                          {user.subscription.stripeCustomerId}
                        </dd>
                      </div>
                    )}

                    {user.subscription.stripeSubscriptionId && (
                      <div className="flex justify-between py-3 px-4">
                        <dt className="text-sm font-medium text-gray-500">Subscription ID</dt>
                        <dd className="text-sm text-gray-900 font-mono">
                          {user.subscription.stripeSubscriptionId}
                        </dd>
                      </div>
                    )}
                  </dl>

                  {/* Action Buttons */}
                  <div className="flex gap-3">
                    {user.subscription.tier !== 'premium' && (
                      <Button
                        onClick={() => handleUpgrade('premium')}
                        disabled={upgrading}
                        color="success"
                        className="flex-1"
                      >
                        <ArrowUpIcon className="h-4 w-4 mr-2" />
                        {upgrading ? 'Upgrading...' : 'Upgrade to Premium'}
                      </Button>
                    )}
                    {user.subscription.tier === 'premium' && (
                      <Button
                        onClick={() => handleUpgrade('free')}
                        disabled={upgrading}
                        color="gray"
                        className="flex-1"
                      >
                        {upgrading ? 'Downgrading...' : 'Downgrade to Free'}
                      </Button>
                    )}
                  </div>

                  {/* Link to Stripe Dashboard */}
                  {user.subscription.stripeCustomerId && (
                    <div className="text-center">
                      <a
                        href={`https://dashboard.stripe.com/customers/${user.subscription.stripeCustomerId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-blue-600 hover:text-blue-800 inline-flex items-center"
                      >
                        View in Stripe Dashboard
                        <ArrowTopRightOnSquareIcon className="h-4 w-4 ml-1" />
                      </a>
                    </div>
                  )}
                </div>
              ) : (
                <Alert color="failure">
                  <div className="text-sm">
                    <p><strong>ERROR:</strong> Subscription data is missing!</p>
                    <p className="mt-2">Debug info:</p>
                    <pre className="mt-2 p-2 bg-gray-100 rounded text-xs overflow-auto">
                      {JSON.stringify({
                        hasUser: !!user,
                        userKeys: user ? Object.keys(user) : [],
                        subscription: user?.subscription
                      }, null, 2)}
                    </pre>
                  </div>
                </Alert>
              )}
            </div>
          </TabItem>

          {/* Data Tab */}
          <TabItem title="Data" icon={ChartBarIcon}>
            <div className="pt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                <Card>
                  <h4 className="text-sm font-medium text-gray-500 mb-2">Total Entries</h4>
                  <p className="text-2xl font-bold text-gray-900">
                    {analytics?.dataEntries?.toLocaleString() || 0}
                  </p>
                </Card>
                <Card>
                  <h4 className="text-sm font-medium text-gray-500 mb-2">API Calls (30d)</h4>
                  <p className="text-2xl font-bold text-gray-900">
                    {analytics?.apiCalls30d?.toLocaleString() || 0}
                  </p>
                </Card>
                <Card>
                  <h4 className="text-sm font-medium text-gray-500 mb-2">Storage Used</h4>
                  <p className="text-2xl font-bold text-gray-900">
                    {storage?.storage?.mb?.toFixed(2) || 0} MB
                  </p>
                </Card>
              </div>
              <div className="rounded-lg bg-blue-50 p-6 text-center">
                <ChartBarIcon className="h-12 w-12 text-blue-500 mx-auto mb-3" />
                <p className="text-gray-700 mb-4">Detailed analytics charts coming soon</p>
                <Button color="blue" onClick={() => toast.info('Charts will be added in next update')}>
                  View Detailed Analytics
                </Button>
              </div>
            </div>
          </TabItem>
        </Tabs>
      </Card>
    </div>
  );
}

